import { m as motion, useScroll } from 'framer-motion';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { useEffect, useRef } from 'react';
import type { Thing } from 'schema-dts';
import { PAGES } from '@/consts';
import { TeamPerson } from '@/entities';
import { Page } from '@/entities/page';
import { AppCompaniesResponse, getCommonPageProps, getCompanies, getCompanyCategories } from '@/shared/api';
import { getAccordions } from '@/shared/api/accordions';
import { getDays } from '@/shared/api/days';
import { getEmployees } from '@/shared/api/employees';
import { getHistory } from '@/shared/api/history';
import { getPageDetail } from '@/shared/api/page-detail';
import { getReasons } from '@/shared/api/reasons';
import { getReviews } from '@/shared/api/reviews';
import { getSelectionStages } from '@/shared/api/selection-stages';
import { getSiteSettings } from '@/shared/api/site-settings';
import { useHeaderThemeValue } from '@/shared/atoms/header-theme';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import AwardsSection, { AwardsSectionProps } from '@/shared/components/sections/AwardsSection';
import HowItWasSection, { HowItWasSectionProps } from '@/shared/components/sections/HowItWasSection';
import MissionSection, { MissionSectionProps } from '@/shared/components/sections/MissionSection';
import OrganizeSimpleSection, { OrganizeSimpleSectionProps } from '@/shared/components/sections/OrganizeSimpleSection';
import PageWelcome from '@/shared/components/sections/PageWelcome';
import QuestionsSection, { QuestionsSectionProps } from '@/shared/components/sections/QuestionsSection';
import ReviewsSection, { ReviewsSectionProps } from '@/shared/components/sections/ReviewsSection';
import SelectionStagesSection, {
    SelectionStagesSectionProps,
} from '@/shared/components/sections/SelectionStagesSection';
import ServicesContent, { ServicesContentProps } from '@/shared/components/sections/ServicesContent';
import TextWithImageSection, {
    TextWithImageSectionProps,
} from '@/shared/components/sections/TextWithImageSection/TextWithImageSection';
import Button from '@/shared/components/shared/Button';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import Link from '@/shared/components/shared/Link';
import NavigationAnchors from '@/shared/components/shared/NavigationAnchors';
import NavigationSections from '@/shared/components/shared/NavigationSections';
import { Anchors, AppNavigation, CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { tp } from '@/shared/lib/typograf';
import { computeEventDatesFromDays, generateEventPageJsonLd } from '@/shared/lib/utils/event-page-json-ld';
import { scrollingPageToAnchor } from '@/shared/lib/utils/scrollingPageToAnchor';
import ApplyRegistrationPopupCombined, {
    APPLY_REGISTRATION_POPUP_NAME,
} from '../../shared/components/popups/ApplyRegistrationPopupСombined/ApplyRegistrationPopupСombined';

const TECHNOLOGICAL_SECTION_IDS = {
    services: 'services-content',
    howItWas: 'howItWas',
    mission: 'mission',
    textWithImage: 'textWithImage',
    organizeSimple: 'organizeSimple',
    awards: 'awards',
    selectionStages: 'selectionStages',
    reviews: 'reviews',
    registration: 'registration',
} as const;

const TechnologicalPage = ({
    meta,
    footerData,
    breadcrumbs,
    page,
    h1,
    navigation,
    bodyClass,
    anchors,
    sectionIds,
    howItWasSectionProps,
    missionSectionProps,
    awardsSectionProps,
    selectionStagesSectionProps,
    contactPersonProps,
    questionsSectionProps,
    reviewsSectionProps,
    shuttleContentProps,
    textWithImageSectionProps,
    organizeSimpleSectionProps,
    eventStartDateIso,
    eventEndDateIso,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
    const ref = useRef(null);
    const theme = useHeaderThemeValue();
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start -30%', 'end end'] });
    const { openPopup } = useSetOpenedPopupsState();

    useEffect(() => {
        scrollingPageToAnchor();
    }, []);

    return (
        <DefaultLayout currentPageId={PAGES.TECHNOLOGICAL} bodyClass={bodyClass}>
            <section className="technological__section" ref={ref}>
                <PageWelcome breadcrumbs={breadcrumbs} title={h1} />
                <NavigationSections navigation={navigation} className="wrapper" />

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <NavigationAnchors
                        theme={theme}
                        navigation={navigation}
                        anchors={anchors}
                        scrollYProgress={scrollYProgress}
                    />
                    <ServicesContent servicesContent={shuttleContentProps} id={sectionIds.services} />
                    {howItWasSectionProps.slider.images.length > 0 && (
                        <HowItWasSection
                            id={sectionIds.howItWas}
                            howItWasSectionProps={howItWasSectionProps}
                            theme="dark"
                        />
                    )}
                    <MissionSection id={sectionIds.mission} missionSectionProps={missionSectionProps} />
                    <TextWithImageSection
                        textWithImageSectionProps={textWithImageSectionProps}
                        id={sectionIds.textWithImage}
                    >
                        <div className="summit__text-with-image__content">
                            {textWithImageSectionProps.text && (
                                <div
                                    className="text-with-image__text text-m "
                                    dangerouslySetInnerHTML={{ __html: tp(textWithImageSectionProps.text) }}
                                ></div>
                            )}
                            {textWithImageSectionProps.description && (
                                <div className="text-with-image__block-bottom">
                                    <div
                                        className="text-with-image__description wysiwyg text-s"
                                        dangerouslySetInnerHTML={{
                                            __html: tp(textWithImageSectionProps.description),
                                        }}
                                    ></div>
                                </div>
                            )}

                            {textWithImageSectionProps.link && textWithImageSectionProps.link.href && (
                                <Button
                                    onClick={() => {
                                        openPopup(APPLY_REGISTRATION_POPUP_NAME);
                                    }}
                                    variant="outline-dark"
                                    className="selection-stages-card-btn"
                                >
                                    {textWithImageSectionProps.link.name}
                                </Button>
                            )}
                        </div>
                    </TextWithImageSection>
                    {organizeSimpleSectionProps && (
                        <OrganizeSimpleSection
                            organizeSimpleSectionProps={organizeSimpleSectionProps}
                            data-bg="#fff"
                            data-theme="dark"
                            id={sectionIds.organizeSimple}
                        />
                    )}
                    {awardsSectionProps.awards.length > 0 && (
                        <AwardsSection id={sectionIds.awards} awardsSectionProps={awardsSectionProps} />
                    )}
                    {selectionStagesSectionProps.cards.length > 0 && (
                        <SelectionStagesSection
                            id={sectionIds.selectionStages}
                            selectionStagesSectionProps={selectionStagesSectionProps}
                        />
                    )}
                    {reviewsSectionProps.cards.length > 0 && (
                        <ReviewsSection
                            reviewsSectionProps={reviewsSectionProps}
                            data-theme="dark"
                            id={sectionIds.reviews}
                            className="js-header-theme-trigger"
                        />
                    )}
                    <QuestionsSection
                        questionsSectionProps={questionsSectionProps}
                        contactPersonProps={contactPersonProps}
                        className="js-header-theme-trigger"
                        data-theme="dark"
                        id={sectionIds.registration}
                    />
                </motion.div>
                <ApplyRegistrationPopupCombined />
            </section>
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-technological"
                data={generateEventPageJsonLd({
                    name: shuttleContentProps.title || h1,
                    descriptionHtml: shuttleContentProps.content,
                    metaDescription: meta.description,
                    pagePath: '/events/technological/',
                    imageSrc: textWithImageSectionProps.img?.src ?? null,
                    startDateIso: eventStartDateIso ?? undefined,
                    endDateIso: eventEndDateIso ?? undefined,
                    breadcrumbs,
                    contacts: footerData,
                })}
            />
        </DefaultLayout>
    );
};

export default TechnologicalPage;

type TechnologicalPageProps = NonUndefined<
    CommonPageProps & {
        eventStartDateIso: string | null;
        eventEndDateIso: string | null;
        page?: Page;
        h1: string;
        howItWasSectionProps: HowItWasSectionProps;
        missionSectionProps: MissionSectionProps;
        textWithImageSectionProps: TextWithImageSectionProps;
        awardsSectionProps: AwardsSectionProps;
        selectionStagesSectionProps: SelectionStagesSectionProps;
        navigation: AppNavigation;
        reviewsSectionProps: ReviewsSectionProps;
        questionsSectionProps: QuestionsSectionProps;
        contactPersonProps: TeamPerson | null;
        anchors: Anchors;
        shuttleContentProps: ServicesContentProps;
        organizeSimpleSectionProps: OrganizeSimpleSectionProps;
        sectionIds: typeof TECHNOLOGICAL_SECTION_IDS;
    }
>;

export const getStaticProps: GetStaticProps<TechnologicalPageProps> = async () => {
    const pageResponse = await getPageDetail({ params: { slug: 'technological' } });
    const [
        commonPageProps,
        companiesResponse,
        historyResponse,
        accordionsResponse,
        selectionStagesResponse,
        employeesResponse,
        reasonsResponse,
        reviewsResponse,
        daysResponse,
        siteSettings,
    ] = await Promise.all([
        getCommonPageProps(pageResponse.data?.id ?? PAGES.TECHNOLOGICAL),
        getCompanyCategories({ params: { page: pageResponse.data?.id ?? PAGES.TECHNOLOGICAL } }).then(
            (categoriesResponse) => {
                if (Array.isArray(categoriesResponse.data) && categoriesResponse.data.length === 0) {
                    return Promise.resolve<AppCompaniesResponse>({
                        status: 'success',
                        data: [],
                    });
                }

                return getCompanies({
                    params: { categories: (categoriesResponse.data ?? []).map((category) => category.id) },
                });
            },
        ),
        getHistory({ params: { page: pageResponse.data?.id ?? PAGES.TECHNOLOGICAL } }),
        getAccordions({ params: { page: pageResponse.data?.id ?? PAGES.TECHNOLOGICAL } }),
        getSelectionStages({ params: { page: pageResponse.data?.id ?? PAGES.TECHNOLOGICAL } }),
        getEmployees({ params: { page: pageResponse.data?.id ?? PAGES.TECHNOLOGICAL } }),
        getReasons({ params: { page: pageResponse.data?.id ?? PAGES.TECHNOLOGICAL } }),
        getReviews({ params: { page: pageResponse.data?.id ?? PAGES.TECHNOLOGICAL } }),
        getDays({ params: { page: pageResponse.data?.id ?? PAGES.TECHNOLOGICAL } }),
        getSiteSettings({
            params: {
                keys: [
                    'technological_first_screen_title',
                    'technological_first_screen_description',
                    'technological_first_screen_document',

                    'technological_history_title',
                    'technological_history_description',
                    'technological_history_link_text',
                    'technological_history_link',

                    'technological_participate_title',
                    'technological_participate_description',
                    'technological_participate_link_text',
                    'technological_participate_link',

                    'technological_about_title',
                    'technological_about_subtitle',
                    'technological_about_description',
                    'technological_about_image',
                    'technological_about_link_text',
                    'technological_about_link',

                    'technological_company_title',
                    'technological_company_description',

                    'technological_directions_title',
                    'technological_directions_document',

                    'technological_selection_stages_title',
                    'technological_selection_stages_documents',

                    'technological_reviews_title',
                    'technological_reviews_description',

                    'technological_form_title',
                    'technological_form_description',
                    'technological_form_link_text',
                    'technological_form_link',
                ],
            },
        }),
    ]);

    const technologicalAnchors: Anchors = [
        {
            id: TECHNOLOGICAL_SECTION_IDS.services,
            name: 'О событии',
        },
        ...((historyResponse.data?.images ?? []).length > 0
            ? [
                  {
                      id: TECHNOLOGICAL_SECTION_IDS.howItWas,
                      name: 'Как было в том году?',
                  },
              ]
            : []),
        ...((reasonsResponse.data ?? []).length > 0
            ? [
                  {
                      id: TECHNOLOGICAL_SECTION_IDS.mission,
                      name: 'Зачем участвовать?',
                  },
              ]
            : []),
        ...((companiesResponse.data ?? []).length > 0
            ? [
                  {
                      id: TECHNOLOGICAL_SECTION_IDS.organizeSimple,
                      name: 'Организаторы',
                  },
              ]
            : []),
        ...((accordionsResponse.data ?? []).length > 0
            ? [
                  {
                      id: TECHNOLOGICAL_SECTION_IDS.awards,
                      name: 'Направления',
                  },
              ]
            : []),
        ...((selectionStagesResponse.data ?? []).length > 0
            ? [
                  {
                      id: TECHNOLOGICAL_SECTION_IDS.selectionStages,
                      name: 'Этапы отбора',
                  },
              ]
            : []),
        ...((reviewsResponse.data ?? []).length > 0
            ? [
                  {
                      id: TECHNOLOGICAL_SECTION_IDS.reviews,
                      name: 'Отзывы',
                  },
              ]
            : []),
        {
            id: TECHNOLOGICAL_SECTION_IDS.registration,
            name: 'Обратная связь',
        },
    ];
    const { startDateIso: eventStartDateIso, endDateIso: eventEndDateIso } = computeEventDatesFromDays(
        daysResponse.data ?? [],
    );

    return {
        props: {
            ...commonPageProps,
            eventStartDateIso: eventStartDateIso ?? null,
            eventEndDateIso: eventEndDateIso ?? null,
            bodyClass: 'technological-page',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'События форума', href: '/events/technological/' },
            ],
            page: pageResponse.data,
            h1: 'События форума',

            navigation: [
                {
                    id: 0,
                    title: 'Премия Территория технологий',
                    href: '/events/premiya-tec/',
                },
                {
                    id: 1,
                    title: 'Технологические дни',
                    href: '/events/technological/',
                },
                {
                    id: 2,
                    title: 'HR-Саммит и Премия',
                    href: '/events/summit/',
                },
                {
                    id: 3,
                    title: 'TNF LIVE',
                    href: '/events/tnf-live/',
                },
            ],
            anchors: technologicalAnchors,
            sectionIds: TECHNOLOGICAL_SECTION_IDS,
            organizeSimpleSectionProps: {
                title: siteSettings.data?.technological_company_title ?? 'Организаторы Технологических дней',
                description:
                    siteSettings.data?.technological_company_description ??
                    `
                    Лидеры рынка ждут <br> технологических решений
                    `,
                partners: (companiesResponse.data ?? []).map((partner) => ({
                    logo: partner.logo,
                    title: partner.title,
                    description: partner.description ?? '',
                    href: partner.href ?? '',
                    categoryId: partner.categoryId,
                })),
            },
            howItWasSectionProps: {
                slider: historyResponse.data ?? { images: [] },
                title: siteSettings.data?.technological_history_title ?? 'Как было в том году?',
                description:
                    siteSettings.data?.technological_history_description ??
                    'Форум прошлого года стал одним из самых значимых событий нефтегазовой промышленности, объединив в Тюмени ведущих экспертов и ключевых игроков отрасли со всей страны',
                link: siteSettings.data?.technological_history_link
                    ? {
                          href: siteSettings.data?.technological_history_link,
                          name: siteSettings.data?.technological_history_link_text ?? '',
                      }
                    : null,
            },
            missionSectionProps: {
                title: siteSettings.data?.technological_participate_title ?? 'Зачем участвовать?',
                subtitle:
                    siteSettings.data?.technological_participate_description ??
                    'Для участия необходимо пройти регистрацию.',
                link: {
                    name: siteSettings.data?.technological_participate_link_text ?? '',
                    href: siteSettings.data?.technological_participate_link ?? '',
                },
                cards: reasonsResponse.data ?? [],
            },
            textWithImageSectionProps: {
                title:
                    siteSettings.data?.technological_about_title ??
                    'Заявка на Техдень попадает в Технологический брокер Нефтегазового кластера',
                text: siteSettings.data?.technological_about_subtitle ?? 'Механизм, работающий за пределами форума',
                description:
                    siteSettings.data?.technological_about_description ??
                    'Нефтегазовый кластер — официальный оператор крупных нефтегазовых компаний: отбирает технологии по их запросам и сопровождает проект на пути от презентации разработки до выхода на ОПИ и внедрение за пределами форума TNF.',
                link: siteSettings.data?.technological_about_link
                    ? {
                          href: siteSettings.data?.technological_about_link,
                          name: siteSettings.data?.technological_about_link_text ?? 'Принять участие',
                      }
                    : null,
                img: siteSettings.data?.technological_about_image ?? null,
            },
            awardsSectionProps: {
                title: siteSettings.data?.technological_directions_title ?? 'Номинации HR-Премии',
                awards: accordionsResponse.data ?? [],
                documents: siteSettings.data?.technological_directions_document
                    ? [
                          {
                              src: siteSettings.data.technological_directions_document.src,
                              title: siteSettings.data.technological_directions_document.title ?? '',
                              alt: siteSettings.data.technological_directions_document.alt ?? '',
                          },
                      ]
                    : [],
            },
            selectionStagesSectionProps: {
                title: siteSettings.data?.technological_selection_stages_title ?? 'Этапы отбора',
                documents: (siteSettings.data?.technological_selection_stages_documents ?? []).map((document) => ({
                    src: document.src,
                    title: document.title ?? '',
                    alt: document.alt ?? '',
                })),
                cards: selectionStagesResponse.data ?? [],
            },
            questionsSectionProps: {
                title: siteSettings.data?.technological_form_title ?? 'Остались вопросы? Напишите нам',
                description: siteSettings.data?.technological_form_description ?? '',
                linkText: siteSettings.data?.technological_form_link_text ?? '',
                href: siteSettings.data?.technological_form_link ?? '',
            },
            reviewsSectionProps: {
                title: siteSettings.data?.technological_reviews_title ?? 'Отзывы об экспо ТНФ',
                subtitle:
                    siteSettings.data?.technological_reviews_description ??
                    'Форум TNF 2023 стал кульминацией года в нефтегазовой промышленности, объединив ведущих экспертов и ключевых игроков отрасли в одном месте',
                cards: reviewsResponse.data ?? [],
            },
            contactPersonProps: employeesResponse.data?.[0] || null,

            shuttleContentProps: {
                title: siteSettings.data?.technological_first_screen_title ?? 'Технологические дни',
                content:
                    siteSettings.data?.technological_first_screen_description ??
                    `
                <p>Технологические дни — это полигон новых идей, где инновационную технологию или продукт можно представить экспертам, которые предоставляют бесценную обратную связь о востребованности и жизнеспособности этого проекта.</p>
                <p>Хотите внедрить свое актуальное для нефтегазовой отрасли решение, но не знаете, как достучаться до нужных специалистов и экспертов крупных корпораций?</p>
                <p>Пройдите отбор на Технологические дни TNF 2024 и презентуйте свой проект потенциальным заказчикам.</p>
                `,
                pdfFile: siteSettings.data?.technological_first_screen_document?.src
                    ? {
                          src: siteSettings.data.technological_first_screen_document.src,
                          title: siteSettings.data.technological_first_screen_document.title ?? '',
                          alt: siteSettings.data.technological_first_screen_document.alt ?? '',
                      }
                    : null,
            },
        } satisfies TechnologicalPageProps,
        revalidate: 60,
    };
};
